<template>
  <div class="korea-global-settings-page" v-loading="loading">
    <div class="page-header">
      <div class="title-section">
        <h2 class="page-title">{{ t('koreaGlobal.title') }}</h2>
        <p class="page-subtitle">{{ t('koreaGlobal.subtitle') }}</p>
      </div>
      <el-button type="primary" :loading="saving" @click="handleSave">{{ t('common.save') }}</el-button>
    </div>

    <template v-if="form">
      <el-card class="section-card" shadow="never">
        <template #header>
          <span class="card-title">{{ t('koreaGlobal.captchaSection') }}</span>
        </template>

        <div class="field-row">
          <span class="field-label">{{ t('common.enable') }}</span>
          <el-switch v-model="form['自动过测谎'].enabled" />
        </div>

        <div class="field-row field-row--lie-contact">
          <span class="field-label">{{ t('koreaGlobal.captchaPhone') }}</span>
          <el-input
            class="gs-input gs-input--phone"
            size="small"
            v-model="form['测谎手机号']"
            :placeholder="t('koreaGlobal.captchaPhonePlaceholder')"
            clearable
          />
        </div>
      </el-card>

      <el-card class="section-card" shadow="never">
        <template #header>
          <span class="card-title">{{ t('koreaGlobal.dingSection') }}</span>
        </template>

        <div class="ding-notice">
          <span class="ding-notice__icon">⚠️</span>
          <p class="ding-notice__text">{{ t('koreaGlobal.dingWarning') }}</p>
        </div>

        <ol class="ding-steps">
          <li>{{ t('koreaGlobal.dingStep1') }}</li>
          <li>{{ t('koreaGlobal.dingStep2') }}</li>
          <li>{{ t('koreaGlobal.dingStep3') }}</li>
          <li>{{ t('koreaGlobal.dingStep4') }}</li>
          <li>{{ t('koreaGlobal.dingStep5') }}</li>
        </ol>

        <div class="field-row field-row--token">
          <span class="field-label">{{ t('koreaGlobal.accessToken') }}</span>
          <el-input
            class="gs-input gs-input--token"
            size="small"
            v-model="form['钉钉'].access_token"
            :placeholder="t('koreaGlobal.tokenPlaceholder')"
            clearable
            show-password
          />
        </div>
        <p class="field-hint">{{ t('koreaGlobal.tokenHint') }}</p>

        <div class="ding-qr-block">
          <span class="field-label ding-qr-block__label">{{ t('koreaGlobal.qrLabel') }}</span>
          <div class="ding-qr-panel">
            <img :src="dingQrUrl" :alt="t('koreaGlobal.qrLabel')" class="ding-qr-image" />
            <p class="ding-qr-caption">{{ t('koreaGlobal.qrCaption') }}</p>
          </div>
        </div>
      </el-card>

      <el-card class="section-card" shadow="never">
        <template #header>
          <span class="card-title">GM训练测谎</span>
        </template>

        <div class="field-row">
          <span class="field-label">AI 选择</span>
          <el-radio-group v-model="form['GM训练测谎'].ai_provider">
            <el-radio
              v-for="item in AI_PROVIDER_OPTIONS"
              :key="item.value"
              :label="item.value"
            >
              {{ item.label }}
            </el-radio>
          </el-radio-group>
        </div>

        <div class="provider-config-block">
          <template v-if="form['GM训练测谎'].ai_provider === AI_PROVIDER_CLAUDE">
            <div class="field-row field-row--token">
              <span class="field-label">API Key</span>
              <el-input
                class="gs-input gs-input--token"
                size="small"
                v-model="form['GM训练测谎'].claude.api_key"
                placeholder="Lemon API Key"
                clearable
                show-password
              />
            </div>
            <div class="field-row field-row--token">
              <span class="field-label">模型</span>
              <el-select v-model="form['GM训练测谎'].claude.model" class="model-select-wide" size="small">
                <el-option
                  v-for="item in CLAUDE_MODEL_OPTIONS"
                  :key="item"
                  :label="item"
                  :value="item"
                />
              </el-select>
            </div>
          </template>
          <template v-else>
            <div class="field-row field-row--token">
              <span class="field-label">API Key</span>
              <el-input
                class="gs-input gs-input--token"
                size="small"
                v-model="form['GM训练测谎'].ark.api_key"
                placeholder="火山方舟 API Key"
                clearable
                show-password
              />
            </div>
            <div class="field-row field-row--token">
              <span class="field-label">模型</span>
              <ArkModelSelect
                v-model="form['GM训练测谎'].ark.model"
                size="small"
                select-class="model-select-wide"
                input-class="gs-input gs-input--token"
              />
            </div>
          </template>
        </div>

        <div class="field-row field-row--top">
          <span class="field-label">AI Prompt</span>
          <div class="prompt-editor">
            <div class="prompt-editor__toolbar">
              <el-button size="small" @click="restoreGmTrainLiePrompt">还原默认</el-button>
            </div>
            <el-input
              v-model="form['GM训练测谎'].prompt"
              type="textarea"
              :rows="18"
              class="gs-textarea"
              placeholder="AI 测谎答题 Prompt"
            />
          </div>
        </div>
        <p class="field-hint field-hint--prompt">
          支持占位符：<code>${role_name}</code>、<code>${vocation_name}</code>、<code>${level}</code>、<code>${问题}</code>、<code>{题库按顺序拼接}</code>。
        </p>
      </el-card>

      <el-card class="section-card" shadow="never">
        <template #header>
          <span class="card-title">{{ t('koreaGlobal.otherSection') }}</span>
        </template>
        <el-checkbox v-model="form['其他']['自动更新']">{{ t('koreaGlobal.autoUpdate') }}</el-checkbox>
      </el-card>
    </template>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { t } from '@/locales/zh'
import { ElMessage } from 'element-plus'
import { getGlobalConfig, saveGlobalConfig } from '../api/configs'
import { defaultGmTrainLieConfig, confirmRestoreDefaultPrompt, ensureGmTrainLieConfig, AI_PROVIDER_OPTIONS, AI_PROVIDER_CLAUDE, AI_PROVIDER_ARK, CLAUDE_MODEL_OPTIONS } from '../constants/gmTrainLiePrompt'
import ArkModelSelect from '../components/ArkModelSelect.vue'
import dingQrUrl from '../../icon/ding.png'

function defaultDingTalk() {
  return {
    access_token: ''
  }
}

function normalizeAccessToken(raw) {
  const s = String(raw ?? '').trim()
  if (!s) return ''
  const matched = s.match(/access_token=([^&\s#]+)/i)
  if (matched && matched[1]) {
    return decodeURIComponent(matched[1].trim())
  }
  return s
}

function defaultLieDetect() {
  return {
    enabled: true
  }
}

function defaultContent() {
  return {
    测谎手机号: '',
    自动过测谎: defaultLieDetect(),
    钉钉: defaultDingTalk(),
    'GM训练测谎': defaultGmTrainLieConfig(),
    其他: {
      自动更新: false
    }
  }
}

function ensureGmTrainLie(form) {
  if (!form['GM训练测谎'] || typeof form['GM训练测谎'] !== 'object') {
    form['GM训练测谎'] = defaultGmTrainLieConfig()
  }
  ensureGmTrainLieConfig(form['GM训练测谎'])
}

function ensureLieDetect(form) {
  if (!form['自动过测谎'] || typeof form['自动过测谎'] !== 'object') {
    form['自动过测谎'] = defaultLieDetect()
  }
}

function deepMerge(base, over) {
  if (!over || typeof over !== 'object') {
    return base
  }
  for (const key of Object.keys(over)) {
    const v = over[key]
    const b = base[key]
    if (
      v &&
      typeof v === 'object' &&
      !Array.isArray(v) &&
      b &&
      typeof b === 'object' &&
      !Array.isArray(b)
    ) {
      deepMerge(b, v)
    } else if (v !== undefined) {
      base[key] = v
    }
  }
  return base
}

function ensureDingTalk(form) {
  if (!form['钉钉'] || typeof form['钉钉'] !== 'object') {
    form['钉钉'] = defaultDingTalk()
  }
}

function buildSavePayload(form) {
  ensureDingTalk(form)
  ensureLieDetect(form)
  ensureGmTrainLie(form)
  const phone = form['测谎手机号']
  const lie = form['自动过测谎']
  const gm = form['GM训练测谎']
  return {
    测谎手机号: phone == null ? '' : String(phone).trim(),
    自动过测谎: {
      enabled: !!lie.enabled
    },
    钉钉: {
      access_token: normalizeAccessToken(form['钉钉'].access_token)
    },
    'GM训练测谎': {
      prompt: gm.prompt == null ? '' : String(gm.prompt),
      ai_provider: gm.ai_provider || 'claude',
      claude: {
        api_key: gm.claude?.api_key == null ? '' : String(gm.claude.api_key).trim(),
        model: gm.claude?.model == null ? '' : String(gm.claude.model).trim()
      },
      ark: {
        api_key: gm.ark?.api_key == null ? '' : String(gm.ark.api_key).trim(),
        model: gm.ark?.model == null ? '' : String(gm.ark.model).trim()
      }
    },
    其他: {
      自动更新: !!form['其他']?.['自动更新']
    }
  }
}

export default {
  name: 'AdventureIslandKoreaGlobalSettings',
  components: { ArkModelSelect },
  setup() {
    const loading = ref(false)
    const saving = ref(false)
    const form = ref(null)

    const load = async () => {
      loading.value = true
      try {
        const res = await getGlobalConfig()
        if (res.code === 200 && res.data) {
          const merged = defaultContent()
          deepMerge(merged, res.data)
          ensureDingTalk(merged)
          ensureLieDetect(merged)
          ensureGmTrainLie(merged)
          merged['钉钉'].access_token = normalizeAccessToken(merged['钉钉'].access_token)
          form.value = merged
        } else {
          ElMessage.error(res.message || t('koreaGlobal.loadFail'))
          form.value = defaultContent()
        }
      } catch (e) {
        console.error(e)
        ElMessage.error(t('koreaGlobal.loadFail'))
        form.value = defaultContent()
      } finally {
        loading.value = false
      }
    }

    const handleSave = async () => {
      if (!form.value) return
      saving.value = true
      try {
        const res = await saveGlobalConfig(buildSavePayload(form.value))
        if (res.code === 200) {
          ElMessage.success(res.message || t('koreaGlobal.saveOk'))
          if (res.data) {
            const merged = defaultContent()
            deepMerge(merged, res.data)
            ensureDingTalk(merged)
            ensureLieDetect(merged)
            ensureGmTrainLie(merged)
            merged['钉钉'].access_token = normalizeAccessToken(merged['钉钉'].access_token)
            form.value = merged
          }
        } else {
          ElMessage.error(res.message || t('koreaGlobal.saveFail'))
        }
      } catch (e) {
        console.error(e)
        ElMessage.error(t('koreaGlobal.saveFail'))
      } finally {
        saving.value = false
      }
    }

    const restoreGmTrainLiePrompt = async () => {
      if (!form.value?.['GM训练测谎']) return
      await confirmRestoreDefaultPrompt((prompt) => {
        form.value['GM训练测谎'].prompt = prompt
      })
    }

    onMounted(() => {
      load()
    })

    return {
      t,
      loading,
      saving,
      form,
      dingQrUrl,
      handleSave,
      restoreGmTrainLiePrompt,
      AI_PROVIDER_OPTIONS,
      AI_PROVIDER_CLAUDE,
      AI_PROVIDER_ARK,
      CLAUDE_MODEL_OPTIONS
    }
  }
}
</script>

<style scoped>
.korea-global-settings-page {
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 0 0 32px;
  box-sizing: border-box;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e8e9ea;
  gap: 16px;
}

.title-section {
  flex: 1;
  min-width: 0;
}

.title-section .page-title {
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  color: #1d1d1f;
}

.title-section .page-subtitle {
  margin: 8px 0 0;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
}

.section-card {
  margin-bottom: 16px;
  border-radius: 12px;
}

.card-title {
  font-weight: 600;
  font-size: 15px;
}

.field-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.field-row--lie-contact {
  flex-wrap: wrap;
}

.field-row--token {
  flex-wrap: wrap;
  margin-top: 8px;
}

.field-label {
  flex: 0 0 120px;
  font-size: 14px;
  color: #374151;
}

.gs-input--phone {
  max-width: 280px;
}

.gs-input--token {
  flex: 1;
  min-width: 200px;
  max-width: 560px;
}

.field-row--top {
  align-items: flex-start;
}

.field-row--top .field-label {
  padding-top: 10px;
}

.field-row--top .field-label {
  padding-top: 10px;
}

.prompt-editor {
  flex: 1;
  min-width: 0;
}

.prompt-editor__toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
}

.field-hint--prompt {
  margin: -4px 0 16px 132px;
}

.field-hint code {
  font-size: 11px;
  background: #f0f2f5;
  padding: 1px 5px;
  border-radius: 4px;
}

.model-select-wide {
  width: 320px;
  max-width: 100%;
}

.provider-config-block {
  margin-bottom: 14px;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
}

.provider-config-title {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 10px;
}

.korea-global-settings-page :deep(.gs-textarea .el-textarea__inner) {
  font-size: 13px;
  line-height: 1.55;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  border-radius: 10px;
  padding: 12px;
}

.field-hint {
  margin: 6px 0 0 132px;
  font-size: 12px;
  color: #9ca3af;
}

.ding-notice {
  display: flex;
  gap: 10px;
  padding: 12px 14px;
  margin-bottom: 14px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 10px;
}

.ding-notice__icon {
  flex-shrink: 0;
  font-size: 18px;
}

.ding-notice__text {
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  color: #92400e;
}

.ding-steps {
  margin: 0 0 16px 20px;
  padding: 0;
  font-size: 13px;
  line-height: 1.65;
  color: #4b5563;
}

.ding-steps li {
  margin-bottom: 6px;
}

.ding-qr-block {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px dashed #e5e7eb;
}

.ding-qr-block__label {
  display: block;
  margin-bottom: 10px;
}

.ding-qr-panel {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  padding: 12px;
  background: #f9fafb;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
}

.ding-qr-image {
  width: 160px;
  height: 160px;
  object-fit: contain;
}

.ding-qr-caption {
  margin: 10px 0 0;
  max-width: 280px;
  font-size: 12px;
  color: #6b7280;
  text-align: center;
  line-height: 1.45;
}
</style>
